import React from 'react';
import SubscriberCard from 'components/common/election/cards/SubscriberCard';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import EditPrimaryModal from 'components/common/election/EditPrimaryModal';
import { OverlaySpinner, useQueryParams } from '@insurancemenu/imenu-components-lib/';
import DynamicFormModal from 'components/common/modals/DynamicFormModal_DEPRECATED';
import { useHistory, useParams } from 'react-router-dom';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import ManageDependentsModal from 'components/common/election/ManageDependentsModal';
import { getClassesAvailableForSelection } from 'global/services-helpers/electionService';
import { createSubscriberPresenter } from 'global/utils/election/electionPresenters';
import { ContentWrapperStyled } from './styles';

const ManageSubscriberContent = observer((): React.ReactNode => {
  const query = useQueryParams();
  const subscriberId = query.get('subId') || '';
  const groupId = query.get('groupId') || '';

  const { periodId } = useParams<{ periodId: string }>();
  const history = useHistory();

  if (!subscriberId) return null;

  const {
    domainStoreV2: { availableClasses },
    manageSubscriberStore: {
      loading,
      subscriber,
      products,
      employeeClasses,
      statistic,
      requiredSubInfoPresenter,
      cartId,
      getSubscriber,
      updateDependentsChoice,
      updateProduct,
      getStatistic,
      editPrimaryInfo,
      manageDependents,
      editPrimaryModalStore,
      manageDependentsModalStore,
    },
    productLineRateModalStore: {
      isProductLineModalOpen,
      modalLoading,
      dynamicForm,
      form,
      openProductLineModal,
      updateSubscriberWithPLineFormAnswers,
      closeProductLineModal,
    },

    subscriberStore: { getLinkToPortfolioDetails },
  } = useRootStore();

  const currentSubscriber = subscriber && toJS(subscriber);
  const convertedProducts = products?.map((product) => toJS(product)).slice();
  const convertedEeClasses = employeeClasses.map((eeClassInfo) => toJS(eeClassInfo)).slice();
  const convertedRequiredSubInfo = requiredSubInfoPresenter ? toJS(requiredSubInfoPresenter) : undefined;
  const subRates = toJS(statistic)?.subRates;
  const subscriberRates = subRates?.[subscriberId] || [];

  const onSavePrimaryInfo = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await editPrimaryModalStore.saveSubscribersChanges(periodId, async (): Promise<void> => {
      await getStatistic(groupId, periodId);
    });
    void getSubscriber(subscriberId);
  };

  const onSaveProductLineRateInfo = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (currentSubscriber) {
      await updateSubscriberWithPLineFormAnswers(periodId, currentSubscriber).then(() => {
        void getSubscriber(subscriberId);
        void getStatistic(groupId, periodId);
      });
    }
  };

  const showDetails = (productLineId: ProductLineId): void => {
    const link = getLinkToPortfolioDetails(currentSubscriber, productLineId, convertedProducts, cartId, periodId);
    history.push(link);
  };

  const onCloseManageDependentsModal = (): void => {
    manageDependentsModalStore.closeManageDependentsModal();
    void getStatistic(groupId, periodId);
    void getSubscriber(subscriberId);
  };

  return (
    <ContentWrapperStyled>
      <OverlaySpinner display={loading} overlay="none" />
      {currentSubscriber && (
        <>
          <SubscriberCard
            subscriber={createSubscriberPresenter(currentSubscriber)}
            productLineRates={subscriberRates}
            products={convertedProducts}
            subscriberClassesMap={{
              employeeClasses: getClassesAvailableForSelection(convertedEeClasses, availableClasses),
            }}
            callbacksMap={{
              editSubscriber: editPrimaryInfo,
              manageDependents,
              updateDependentsChoice,
              updateProduct,
              onPLineRateChange: (formId: string, productId: string): void => {
                void openProductLineModal(currentSubscriber, formId, productId);
              },
              onShowDetails: showDetails,
            }}
            requiredSubInfo={convertedRequiredSubInfo}
          />

          {/* Modals */}
          <EditPrimaryModal modalStore={editPrimaryModalStore} onAction={onSavePrimaryInfo} isEditable />
          <ManageDependentsModal
            modalStore={manageDependentsModalStore}
            subscriber={currentSubscriber}
            onClose={onCloseManageDependentsModal}
          />
          <DynamicFormModal
            show={isProductLineModalOpen}
            onClose={closeProductLineModal}
            onAction={onSaveProductLineRateInfo}
            dynamicForm={dynamicForm}
            form={form}
            loading={modalLoading}
            isEditable
            modalMaxWidth="90rem"
          />
        </>
      )}
    </ContentWrapperStyled>
  );
});

export default ManageSubscriberContent;
